import "./ProgressGauge.css";

const SECTIONS = [
  {
    label: "Foundation",
    color: "#E6C121",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/foundation.svg",
    points: 200,
  },
  {
    label: "AI Aware",
    color: "#F99435",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/ai_aware.svg",
    points: 100,
  },
  {
    label: "AI Practitioner",
    color: "#3DC672",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/ai_practitioner.svg",
    points: 100,
  },
  {
    label: "AI Strategist",
    color: "#41A89E",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/ai_strategist.svg",
    points: 100,
  },
  {
    label: "AI Native",
    color: "#2F679D",
    icon: process.env.PUBLIC_URL + "/Assets/Scoreboard/ai_native.svg",
    points: 100,
  },
];

const WIDTH = 800;
const HEIGHT = 430;

const CX = 400;
const CY = 420;

const OUTER_R = 350;
const INNER_R = 225;

const SCORE_R = 142;

const MAX_SCORE = SECTIONS.reduce((sum, section) => sum + section.points, 0);

function polarToCartesian(cx, cy, r, angle) {
  const rad = (Math.PI / 180) * angle;

  return {
    x: cx + r * Math.cos(rad),
    y: cy - r * Math.sin(rad),
  };
}

function describeArc(cx, cy, outerR, innerR, startAngle, endAngle) {
  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, startAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);

  const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;

  return `
    M ${outerStart.x} ${outerStart.y}
    A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}
    L ${innerEnd.x} ${innerEnd.y}
    A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}
    Z
  `;
}

function describeStrokeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;

  return `
    M ${start.x} ${start.y}
    A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}
  `;
}

function pointOnArc(angle, radius) {
  return polarToCartesian(CX, CY, radius, angle);
}

export default function ProgressGauge({ score = MAX_SCORE }) {
  const safeScore = Math.max(0, Math.min(score, MAX_SCORE));

  const totalSections = SECTIONS.length;
  const segmentSize = 180 / totalSections;
  const sectionGap = 2;
  const PROGRESS_R = 178;

  const progressAngle = 180 - (safeScore / MAX_SCORE) * 180;

  const boundaries = [];
  let runningPoints = 0;

  for (let i = 0; i < SECTIONS.length - 1; i += 1) {
    runningPoints += SECTIONS[i].points;
    boundaries.push(runningPoints);
  }

  let needleAngle = 180 - (safeScore / MAX_SCORE) * 180;

  boundaries.forEach((boundary, index) => {
    if (safeScore === boundary) {
      needleAngle = 180 - (index + 1) * segmentSize;
    }
  });

  const NEEDLE_WIDTH = 20;
  const NEEDLE_HEIGHT = 30;
  const NEEDLE_RADIUS = PROGRESS_R + 25;
  const needleBase = pointOnArc(needleAngle + 21, NEEDLE_RADIUS);
  const needleTipX = needleBase.x;
  const needleTipY = needleBase.y;
  const needleX = needleTipX - NEEDLE_WIDTH / 2;
  const needleY = needleTipY;
  const needleRotation = 40 - needleAngle;

  return (
    <div className="progressGauge">
      <svg
        className="progressGaugeSvg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`Progress score ${safeScore}`}
      >
        <defs>
          {SECTIONS.map((section, index) => {
            const startAngle = 180 - index * segmentSize - sectionGap;
            const endAngle = 180 - (index + 1) * segmentSize + sectionGap;

            return (
              <path
                key={`labelPath-${index}`}
                id={`labelPath-${index}`}
                d={describeStrokeArc(CX, CY, 295, startAngle, endAngle)}
                fill="none"
              />
            );
          })}

          {SECTIONS.map((section, index) => {
            const startAngle = 180 - index * segmentSize - sectionGap;
            const endAngle = 180 - (index + 1) * segmentSize + sectionGap;

            return (
              <path
                key={`valuePath-${index}`}
                id={`valuePath-${index}`}
                d={describeStrokeArc(CX, CY, 265, startAngle, endAngle)}
                fill="none"
              />
            );
          })}

          <filter id="iconGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="
        1 0 0 0 1
        0 1 0 0 1
        0 0 1 0 1
        0 0 0 0.45 0
      "
              result="whiteGlow"
            />
            <feMerge>
              <feMergeNode in="whiteGlow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter
            id="circleShadow"
            x="-80%"
            y="-80%"
            width="260%"
            height="260%"
          >
            <feDropShadow
              dx="5"
              dy="0"
              stdDeviation="10"
              floodColor="#000000"
              floodOpacity="0.75"
            />
          </filter>

          <linearGradient
            id="scoreGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#8d3cbc" />
            <stop offset="100%" stopColor="#8d3cbc" />
          </linearGradient>

          <filter
            id="progressArcShadow"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feDropShadow
              dx="1"
              dy="3"
              stdDeviation="2"
              floodColor="#000"
              floodOpacity="0.5"
            />
          </filter>
        </defs>

        <path
          d={describeArc(CX, CY, 308, 267, 180 - sectionGap, 0 + sectionGap)}
          fill="#c6c6c6"
        />

        {SECTIONS.map((section, index) => {
          const startAngle = 180 - index * segmentSize - sectionGap;
          const endAngle = 180 - (index + 1) * segmentSize + sectionGap;

          return (
            <path
              key={section.label}
              d={describeArc(CX, CY, OUTER_R, INNER_R, startAngle, endAngle)}
              fill={section.color}
            />
          );
        })}

        {SECTIONS.map((section, index) => {
          const midAngle = 180 - index * segmentSize - segmentSize / 2;
          const lineStart = pointOnArc(midAngle, INNER_R);
          const lineEnd = pointOnArc(midAngle, PROGRESS_R + 8);

          return (
            <line
              key={`${section.label}-guide-line`}
              x1={lineStart.x}
              y1={lineStart.y}
              x2={lineEnd.x}
              y2={lineEnd.y}
              stroke="#000000"
              strokeWidth="2"
              strokeDasharray="4 6"
              strokeLinecap="round"
              opacity="0.9"
            />
          );
        })}

        <path
          d={describeStrokeArc(CX, CY, PROGRESS_R, 180, 0)}
          className="progressArcTrack"
          filter="url(#progressArcShadow)"
        />

        {(() => {
          let runningPoints = 0;

          return SECTIONS.map((section, index) => {
            const startScore = runningPoints;
            runningPoints += section.points;
            const endScore = runningPoints;

            if (safeScore <= startScore) return null;

            const filledScoreInSection =
              Math.min(safeScore, endScore) - startScore;
            const sectionFillRatio = filledScoreInSection / section.points;

            if (sectionFillRatio <= 0) return null;

            const sectionStartAngle = 180 - index * segmentSize;
            const sectionEndAngle =
              sectionStartAngle - segmentSize * sectionFillRatio;

            return (
              <path
                key={`progress-fill-${section.label}`}
                d={describeStrokeArc(
                  CX,
                  CY,
                  PROGRESS_R,
                  sectionStartAngle,
                  sectionEndAngle,
                )}
                stroke={section.color}
                className="progressArcFill"
                fill="none"
              />
            );
          });
        })()}

        {SECTIONS.map((section, index) => {
          return (
            <g key={`${section.label}-text`}>
              <text className="gaugeLabel">
                <textPath
                  xlinkHref={`#labelPath-${index}`}
                  startOffset="50%"
                  textAnchor="middle"
                >
                  {section.label}
                </textPath>
              </text>

              <text className="gaugeValue">
                <textPath
                  xlinkHref={`#valuePath-${index}`}
                  startOffset="50%"
                  textAnchor="middle"
                >
                  {section.points}
                </textPath>
              </text>
            </g>
          );
        })}

        {SECTIONS.map((section, index) => {
          const midAngle = 180 - index * segmentSize - segmentSize / 2;
          const iconPoint = pointOnArc(midAngle, 345);

          return (
            <g key={`${section.label}-icon`} filter="url(#iconGlow)">
              <circle
                cx={iconPoint.x}
                cy={iconPoint.y}
                r="20"
                fill={section.color}
                stroke="#ffffff"
                strokeWidth="8"
                filter="url(#circleShadow)"
              />

              <image
                href={section.icon}
                x={iconPoint.x - 10}
                y={iconPoint.y - 10}
                width="20"
                height="20"
                preserveAspectRatio="xMidYMid meet"
              />
            </g>
          );
        })}

        <path
          d={`
            M ${CX - SCORE_R} ${CY}
            A ${SCORE_R} ${SCORE_R} 0 0 1 ${CX + SCORE_R} ${CY}
            L ${CX + SCORE_R} ${HEIGHT}
            L ${CX - SCORE_R} ${HEIGHT}
            Z
          `}
          fill="url(#scoreGradient)"
        />

        <image
          href={process.env.PUBLIC_URL + "/Assets/Scoreboard/score-hand.svg"}
          x={needleX}
          y={needleY}
          width={NEEDLE_WIDTH}
          height={NEEDLE_HEIGHT}
          preserveAspectRatio="xMidYMid meet"
          transform={`rotate(${needleRotation} ${needleTipX} ${needleTipY})`}
        />

        <text
          x={CX}
          y="355"
          className="scoreNumber"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {safeScore}
        </text>

        <text
          x={CX}
          y="393"
          className="scoreText"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          Your Score
        </text>
      </svg>
    </div>
  );
}
